import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { GenericEventFeed } from './GenericEventFeed';
import { useEventsData } from '@/features/EventsListLayout/hooks';
import { useAddEventToReportMenuItem, useOpenInNewWindow } from '@/hooks';
import { useSortFromUrl } from '@/hooks/useSortFromUrl';
import { getDetectionLabel } from '@/utils/detection-classes';
import { SortOption } from '@/utils/query-filters';
import { AppRoutes } from '@/constants/routes';
import type { FeedEmptyStateProps } from './components';

interface RecentEventsFeedProps {
    titleContent?: ReactNode;
    emptyState?: FeedEmptyStateProps;
}

export function RecentEventsFeed({
    titleContent,
    emptyState,
}: RecentEventsFeedProps) {
    const { t } = useTranslation(I18nNamespace.EVENTS);
    const navigate = useNavigate();
    const { openEvent } = useOpenInNewWindow();
    const addToReportItem = useAddEventToReportMenuItem();
    const [sortOption] = useSortFromUrl(SortOption.DATE_NEWEST);

    const { events, isLoading, isLoadingMore, hasMore, loadMore } =
        useEventsData({ pageKey: 'recent-events', sortOption });

    return (
        <GenericEventFeed
            containerTitle={t('sidebar.recentEvents')}
            titleContent={titleContent}
            onViewAll={() => navigate(AppRoutes.EVENTS)}
            items={events}
            isLoading={isLoading}
            emptyState={emptyState}
            itemKey={(event) => event.id}
            itemEvent={(event) => event}
            cardTitle={t('page.event')}
            itemDescription={(event) => getDetectionLabel(t, event.classId)}
            itemActionMenuItems={(_, event) => addToReportItem(event)}
            onNavigate={(event, clickEvent) =>
                openEvent(event.id, clickEvent, {
                    state: { feedContext: { type: 'recentEvents' } },
                })
            }
            pagination={{ hasMore, isLoadingMore, onLoadMore: loadMore }}
            quickViewContextType="event-feed"
        />
    );
}
